import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ShieldCheck, Mail, Smartphone, ChevronRight, Loader2 } from 'lucide-react';
import { meApi } from '../../api/endpoints/me';
import { authApi } from '../../api/endpoints/auth';
import { useAuthStore } from '../../stores/authStore';
import { useAuth } from '../../hooks/useAuth';
import { handleApiError } from '../../api/errorMapping';
import { HelpHint } from '@/components/ui/HelpHint';
import { HELP_TEXT } from '@/lib/helpText';

interface Props {
    /** Si presente → primer login pre-cookies. Si ausente → elección post-token (F-12) / re-setup. */
    session?: string;
}

/**
 * Selector de método MFA (2º factor), sin opción preseleccionada por default (REGLA 5, F-12).
 *
 * - Autenticado (sin `session`): pantalla de elección de primer acceso / re-setup. El user ya
 *   tiene tokens (post-NEW_PASSWORD o post-EMAIL_OTP), así que es self-service:
 *     · EMAIL → `POST /mfa/choose-email` (sin OTP previo): marca EMAIL + activa el user.
 *     · TOTP → wizard de enrolamiento existente (`/me/security/mfa-totp-setup`). Gated F-3.
 *   Incluye "Cerrar sesión" como escape (el setup es forzado y standalone — sin esto el user
 *   quedaba atrapado).
 * - Primer login (con `session`): el backend NO enrola MFA durante el login (siempre EMAIL_OTP);
 *   mostramos un mensaje informativo.
 */
export function MfaMethodChooser({ session }: Props) {
    const navigate = useNavigate();
    const refresh = useAuthStore((s) => s.refresh);
    const clear = useAuthStore((s) => s.clear);
    const { user } = useAuth();
    const isFirstLogin = session !== undefined && session.length > 0;

    // Métodos PERMITIDOS para este usuario (data-driven, reemplaza el hardcode isPlatform): solo
    // ofrecemos los que la security policy del tenant habilita. No se consulta en primer login
    // (con `session`), ahí el chooser solo muestra el mensaje informativo (sin botones de método).
    const optionsQ = useQuery({
        queryKey: ['me', 'security', 'mfa', 'options'],
        queryFn: () => meApi.getMfaOptions(),
        enabled: !isFirstLogin,
    });

    const chooseEmailMut = useMutation({
        mutationFn: () => meApi.chooseEmailMfa(),
        onSuccess: async () => {
            // Rehidratar el perfil ANTES de navegar: el guard ve mfaSetupRequired=false y no rebota.
            await refresh();
            await navigate({ to: '/' });
        },
        onError: handleApiError,
    });

    // DEC-RBAC-084: elegir SMS como 2º factor (sin OTP previo, igual que choose-email). Solo se ofrece
    // si el backend listó SMS_OTP en allowedMethods (implica que el user tiene teléfono).
    const chooseSmsMut = useMutation({
        mutationFn: () => meApi.chooseSmsMfa(),
        onSuccess: async () => {
            await refresh();
            await navigate({ to: '/' });
        },
        onError: handleApiError,
    });

    const handleLogout = async (): Promise<void> => {
        const ids = user ? { cognitoSub: user.cognitoSub, tenantId: user.tenantId } : undefined;
        await authApi.logout(ids).catch(() => undefined);
        clear();
        await navigate({ to: '/login' });
    };

    if (isFirstLogin) {
        return (
            <div className="w-full max-w-md rounded-2xl border bg-card p-8 text-center shadow-sm">
                <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-brand-accent/10">
                    <Mail className="size-6 text-brand-accent" />
                </div>
                <h1 className="text-xl font-semibold">Verificación en el inicio de sesión</h1>
                <p className="mt-2 text-sm text-muted-foreground">
                    Tu cuenta usa verificación por correo cuando inicias sesión. Te enviamos un
                    código cada vez que entras.
                </p>
                <p className="mt-2 text-sm text-muted-foreground">
                    Si quieres usar una aplicación de autenticación, vas a poder configurarla desde
                    tu perfil una vez que ingreses.
                </p>
            </div>
        );
    }

    const pending = chooseEmailMut.isPending || chooseSmsMut.isPending;

    // Fallback FAIL-OPEN: ante error de fetch o `allowedMethods` vacío/ausente, ofrecemos AMBOS
    // métodos base (EMAIL/TOTP). Nunca dejamos al usuario sin poder configurar su 2º factor por una
    // falla de este endpoint (el setup es forzado y standalone; el backend re-enforza la policy al enrolar).
    const allowedRaw = optionsQ.data?.allowedMethods ?? [];
    const showTotp = allowedRaw.length === 0 || allowedRaw.includes('TOTP');
    const showEmail = allowedRaw.length === 0 || allowedRaw.includes('EMAIL_OTP');
    // DEC-RBAC-084: SMS NO entra en el fail-open (requiere teléfono; el backend solo lo lista si el
    // user lo tiene). Se ofrece únicamente si el endpoint lo devolvió explícito.
    const showSms = allowedRaw.includes('SMS_OTP');

    return (
        <div className="w-full max-w-md rounded-2xl border bg-card p-8 shadow-sm">
            <div className="mb-6 text-center">
                <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-brand-accent/10">
                    <ShieldCheck className="size-6 text-brand-accent" />
                </div>
                <h1 className="flex items-center justify-center gap-1.5 text-xl font-semibold">
                    Configura tu segundo factor
                    <HelpHint text={HELP_TEXT.mfa} />
                </h1>
                <p className="mt-1.5 text-sm text-muted-foreground">
                    Agrega una capa extra de seguridad. Elige cómo verificar tu identidad al
                    ingresar.
                </p>
            </div>

            <div className="space-y-3">
                {showTotp && (
                    <button
                        type="button"
                        onClick={() => {
                            void navigate({ to: '/me/security/mfa-totp-setup' });
                        }}
                        disabled={pending}
                        className="flex w-full items-center gap-4 rounded-xl border border-border p-4 text-left transition-colors hover:border-brand-accent hover:bg-brand-accent/5 disabled:opacity-50"
                    >
                        <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-brand-accent/10">
                            <ShieldCheck className="size-5 text-brand-accent" />
                        </div>
                        <div className="flex-1">
                            <div className="text-sm font-medium">Aplicación de autenticación</div>
                            <p className="text-xs text-muted-foreground">
                                Usa una app como Google Authenticator o Authy.
                            </p>
                        </div>
                        <ChevronRight className="size-5 shrink-0 text-muted-foreground" />
                    </button>
                )}

                {showEmail && (
                    <button
                        type="button"
                        onClick={() => {
                            chooseEmailMut.mutate();
                        }}
                        disabled={pending}
                        className="flex w-full items-center gap-4 rounded-xl border border-border p-4 text-left transition-colors hover:border-brand-accent hover:bg-brand-accent/5 disabled:opacity-50"
                    >
                        <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-brand-accent/10">
                            <Mail className="size-5 text-brand-accent" />
                        </div>
                        <div className="flex-1">
                            <div className="text-sm font-medium">Código por correo</div>
                            <p className="text-xs text-muted-foreground">
                                Te enviamos un código a tu correo en cada inicio de sesión.
                            </p>
                        </div>
                        {pending ? (
                            <Loader2 className="size-5 shrink-0 animate-spin text-muted-foreground" />
                        ) : (
                            <ChevronRight className="size-5 shrink-0 text-muted-foreground" />
                        )}
                    </button>
                )}

                {showSms && (
                    <button
                        type="button"
                        onClick={() => {
                            chooseSmsMut.mutate();
                        }}
                        disabled={pending}
                        className="flex w-full items-center gap-4 rounded-xl border border-border p-4 text-left transition-colors hover:border-brand-accent hover:bg-brand-accent/5 disabled:opacity-50"
                    >
                        <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-brand-accent/10">
                            <Smartphone className="size-5 text-brand-accent" />
                        </div>
                        <div className="flex-1">
                            <div className="text-sm font-medium">Código por SMS</div>
                            <p className="text-xs text-muted-foreground">
                                Te enviamos un código a tu teléfono en cada inicio de sesión.
                            </p>
                        </div>
                        {pending ? (
                            <Loader2 className="size-5 shrink-0 animate-spin text-muted-foreground" />
                        ) : (
                            <ChevronRight className="size-5 shrink-0 text-muted-foreground" />
                        )}
                    </button>
                )}
            </div>

            <div className="mt-6 text-center">
                <button
                    type="button"
                    onClick={() => {
                        void handleLogout();
                    }}
                    className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                >
                    Cerrar sesión
                </button>
            </div>
        </div>
    );
}
